import React from 'react';
import {Link} from 'react-router-dom';

import AdminLayout from 'Pages/Admin/AdminLayout';

import {PrimaryButton, SecondaryButton} from 'Components/Button';
import {Alert, Loading, Pagination} from 'Components/Partials';
import {Input, Select} from 'Components/Form';
import {TickTable, TickHeader, TickRow, TickTextCell, TickCell} from 'Components/Services/TickTable';

import User from 'Services/User';
import FormsApi from 'Services/Api/Admin/Services/Forms';

export default class Forms extends React.Component {
    /**
     * @var views
     * @type {array}
     */
    views = [
        {label: 'All forms', value: 'all'},
        {label: 'Has a suggestion not yet applied', value: 'suggested'},
        {label: 'Set to "All services"', value: 'all_services'},
        {label: 'Tagged with a service', value: 'tagged'},
    ];

    /**
     * @var categories
     * @type {array}
     */
    categories = [
        {key: 'attach_to_service_user', label: 'Narrative'},
        {key: 'health_and_safety', label: 'H&S'},
        {key: 'quality_assurance', label: 'QA'},
        {key: 'medication_management', label: 'Medication'},
        {key: 'staff_form', label: 'Staff'},
    ];

    /**
     * @var state
     */
    state = {
        loading: true,
        working: false,
        alert: null,
        forms: [],
        changes: {},
        search: '',
        view: 'all',
        page: 1,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        this.loadForms();
    };

    /**
     * @method services
     * @return {array}
     */
    get services() {
        return User.data?.service_access?.services ?? [];
    }

    /**
     * @method loadForms
     */
    loadForms = async () => {
        this.setState({loading: true});

        const response = await FormsApi.get(null);

        if (response.success) {
            this.setState({loading: false, forms: response.data.data});
        }
    };

    /**
     * Current values for a form, including unsaved changes.
     *
     * @method formValues
     * @param {object} form
     * @return {object}
     */
    formValues = (form) => {
        return this.state.changes[form.id] ?? {
            id: form.id,
            all_services: form.all_services,
            service_ids: form.service_ids,
        };
    };

    /**
     * @method setValues
     * @param {object} form
     * @param {object} values
     */
    setValues = (form, values) => {
        this.setState({
            changes: {...this.state.changes, [form.id]: {...this.formValues(form), ...values}},
        });
    };

    /**
     * @method toggleService
     * @param {object} form
     * @param {string} service_id
     */
    toggleService = (form, service_id) => {
        const ids = this.formValues(form).service_ids;

        this.setValues(form, {
            all_services: false,
            service_ids: ids.includes(service_id) ? ids.filter(id => id !== service_id) : [...ids, service_id],
        });
    };

    /**
     * @method acceptSuggestion
     * @param {object} form
     */
    acceptSuggestion = (form) => {
        this.setValues(form, {all_services: false, service_ids: form.suggestion.service_ids});
    };

    /**
     * @method acceptAllSuggestions
     */
    acceptAllSuggestions = () => {
        const changes = {...this.state.changes};

        this.visibleForms()
            .filter(form => form.suggestion && !this.suggestionApplied(form))
            .forEach(form => {
                changes[form.id] = {id: form.id, all_services: false, service_ids: form.suggestion.service_ids};
            });

        this.setState({changes});
    };

    /**
     * @method suggestionApplied
     * @param {object} form
     * @return {boolean}
     */
    suggestionApplied = (form) => {
        const values = this.formValues(form);

        return !values.all_services &&
            values.service_ids.length === form.suggestion.service_ids.length &&
            form.suggestion.service_ids.every(id => values.service_ids.includes(id));
    };

    /**
     * @method visibleForms
     * @return {array}
     */
    visibleForms = () => {
        const {forms, search, view} = this.state;

        return forms.filter(form => {
            const values = this.formValues(form);

            if (search && !form.title.toLowerCase().includes(search.toLowerCase())) {
                return false;
            }

            switch (view) {
                case 'suggested':
                    return form.suggestion && !this.suggestionApplied(form);
                case 'all_services':
                    return values.all_services;
                case 'tagged':
                    return !values.all_services;
                default:
                    return true;
            }
        });
    };

    /**
     * @method handleSave
     */
    handleSave = async () => {
        const forms = Object.values(this.state.changes);

        this.setState({working: true, alert: null});

        const response = await FormsApi.save(forms);

        if (response.success) {
            this.setState({
                working: false,
                changes: {},
                alert: {type: 'success', message: `Saved ${forms.length} form${forms.length === 1 ? '' : 's'}.`},
            }, this.loadForms);
        } else {
            this.setState({
                working: false,
                alert: {type: 'error', message: response.message, errors: response.errors},
            });
        }
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {loading, working, alert, forms, changes, search, view} = this.state;
        const perPage = 50;
        const visible = this.visibleForms();
        const lastPage = Math.max(1, Math.ceil(visible.length / perPage));
        const page = Math.min(this.state.page, lastPage);
        const pageForms = visible.slice((page - 1) * perPage, page * perPage);
        const changeCount = Object.keys(changes).length;
        const pendingSuggestions = visible.filter(form => form.suggestion && !this.suggestionApplied(form)).length;

        return (
            <AdminLayout title="Form Services">
                <div className="p-6 space-y-6">
                    <p className="text-sm">
                        Tag each form with the services that use it, or leave it on "All services". Staff only see
                        forms for their own services. Suggestions come from the service or house named in the
                        form's title.
                    </p>

                    {alert !== null && (<Alert {...alert} />)}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <Input
                            label="Search"
                            id="search"
                            value={search}
                            onChange={(v) => this.setState({search: v, page: 1})}
                        />
                        <Select
                            label="Show"
                            id="view"
                            value={view}
                            options={this.views}
                            onChange={(v) => this.setState({view: v ?? 'all', page: 1})}
                        />
                        <div>
                            <SecondaryButton
                                text={`Accept ${pendingSuggestions} suggestion${pendingSuggestions === 1 ? '' : 's'}`}
                                disabled={pendingSuggestions === 0}
                                onClick={this.acceptAllSuggestions}
                            />
                        </div>
                    </div>

                    {loading && (<Loading/>)}

                    {!loading && (
                        <div>
                            <p className="text-sm mb-2">
                                {visible.length === forms.length ? `${forms.length} forms` : `${visible.length} of ${forms.length} forms match`}
                                {visible.length > 0 && ` · showing ${(page - 1) * perPage + 1} to ${Math.min(visible.length, page * perPage)}`}.
                                {Object.keys(changes).length > 0 && ' Ticks on other pages are kept until you save.'}
                            </p>

                            <TickTable
                                head={
                                    <tr>
                                        <TickHeader className="min-w-[18rem]">Form</TickHeader>
                                        <TickHeader>Type</TickHeader>
                                        <TickHeader tick>All services</TickHeader>
                                        {this.services.map(service => (
                                            <TickHeader key={service.id} tick>{service.name}</TickHeader>
                                        ))}
                                        <TickHeader className="min-w-[14rem]">Suggestion</TickHeader>
                                    </tr>
                                }
                            >
                                {visible.length === 0 && (
                                    <tr><td className="py-4 px-3" colSpan={4 + this.services.length}>No matching forms.</td></tr>
                                )}
                                {pageForms.map((form, index) => {
                                    const values = this.formValues(form);

                                    return (
                                        <TickRow key={form.id} index={index} changed={!!changes[form.id]}>
                                            <TickTextCell>
                                                <Link to={`/admin/cms/elements/show/${form.id}`} className="font-bold hover:underline">
                                                    {form.title || form.identifier}
                                                </Link>
                                            </TickTextCell>
                                            <TickTextCell>
                                                <div className="flex flex-wrap gap-1">
                                                    {this.categories.filter(c => form[c.key]).map(c => (
                                                        <span key={c.key} className="text-xs bg-gray-200 rounded-full px-2 py-0.5 whitespace-nowrap">
                                                            {c.label}
                                                        </span>
                                                    ))}
                                                </div>
                                            </TickTextCell>
                                            <TickCell
                                                label={`${form.title}: All services`}
                                                checked={values.all_services}
                                                onChange={() => this.setValues(form, {
                                                    all_services: !values.all_services,
                                                    service_ids: !values.all_services ? [] : values.service_ids,
                                                })}
                                            />
                                            {this.services.map(service => (
                                                <TickCell
                                                    key={service.id}
                                                    label={`${form.title}: ${service.name}`}
                                                    checked={!values.all_services && values.service_ids.includes(service.id)}
                                                    onChange={() => this.toggleService(form, service.id)}
                                                />
                                            ))}
                                            <TickTextCell className="text-xs">
                                                {form.suggestion && (
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span>{form.suggestion.reason}</span>
                                                        {this.suggestionApplied(form) ? (
                                                            <span className="text-green-800 font-bold">Applied</span>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="bg-app-leading text-white font-bold rounded-full px-3 py-1"
                                                                onClick={() => this.acceptSuggestion(form)}
                                                            >
                                                                Accept
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </TickTextCell>
                                        </TickRow>
                                    );
                                })}
                            </TickTable>

                            {lastPage > 1 && (
                                <Pagination
                                    className="mt-4"
                                    page={page}
                                    pageCount={lastPage}
                                    goToPage={(next) => this.setState({page: next})}
                                />
                            )}
                        </div>
                    )}

                    <div className="flex items-center gap-4">
                        <PrimaryButton
                            text={working ? 'Saving...' : `Save${changeCount ? ` (${changeCount})` : ''}`}
                            disabled={working || changeCount === 0}
                            onClick={this.handleSave}
                        />
                        {changeCount > 0 && Object.values(changes).some(c => !c.all_services && c.service_ids.length === 0) && (
                            <span className="text-sm text-red-700">
                                Some forms have no service ticked. Tick one, or "All services".
                            </span>
                        )}
                    </div>
                </div>
            </AdminLayout>
        );
    }
}
